import { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faChevronUp, faTriangleExclamation } from "@fortawesome/free-solid-svg-icons";
import styles from "./DayCard.module.css";

/**
 * DayCard — card colapsável de um dia de treino (nome, exercícios, séries/reps/
 * descanso). Extraído de SelfWorkoutModal.jsx (era privado de lá) pra ser
 * reaproveitado também pelo MigrateWorkoutModal.jsx (fluxo de migração/transcrição
 * de treino existente — ver docs/07-issues.md). Qualquer mudança visual aqui
 * afeta os dois fluxos.
 */
const DAY_LABELS = ["Segunda", "Terça", "Quarta", "Quinta", "Sexta", "Sábado", "Dom"];

function formatReps(repsStr) {
  if (!repsStr) return null;
  const parts = repsStr.split(",").map(s => s.trim()).filter(Boolean);
  const unique = [...new Set(parts)];
  return unique.length === 1 ? `${parts.length}×${unique[0]}` : repsStr;
}

function muscleGroupLabel(mg) {
  const map = {
    peito: "Peito", costas: "Costas", ombros: "Ombros", biceps: "Bíceps",
    triceps: "Tríceps", quadriceps: "Quadríceps", posterior_coxa: "Post. Coxa",
    gluteos: "Glúteos", panturrilhas: "Panturrilhas", core: "Core", lombar: "Lombar", obliquos: "Oblíquos",
    adutores: "Adutores", trapezio: "Trapézio", antebraco: "Antebraço",
  };
  return map[mg] || mg;
}

/**
 * @param {object} day - {name, suggested_day, suggested_day_index, exercises}
 * @param {number} index - posição na lista (fallback de rótulo)
 * @param {string} [warning] - aviso não-bloqueante (ex: sanity_flags da migração)
 *   exibido dentro do card, aberto por padrão quando presente.
 */
export default function DayCard({ day, index, warning }) {
  const [open, setOpen] = useState(!!warning || true);
  const dayLabel = DAY_LABELS[day.suggested_day_index] ?? day.suggested_day ?? `Dia ${index + 1}`;

  return (
    <div className={styles.dayCard}>
      <button type="button" className={styles.dayCardHead} onClick={() => setOpen(o => !o)}>
        <div className={styles.dayCardLeft}>
          <span className={styles.dayBadge}>{dayLabel}</span>
          <span className={styles.dayName}>{day.name}</span>
          <span className={styles.dayMeta}>{day.exercises?.length || 0} exercícios</span>
        </div>
        <FontAwesomeIcon icon={open ? faChevronUp : faChevronDown} className={styles.dayChevron} />
      </button>

      {open && warning && (
        <div className={styles.dayWarning}>
          <FontAwesomeIcon icon={faTriangleExclamation} className={styles.dayWarningIcon} aria-hidden="true" />
          <span>{warning}</span>
        </div>
      )}

      {open && (
        <div className={styles.exerciseList}>
          {(day.exercises || []).map((ex, i) => (
            <div key={i} className={styles.exerciseRow}>
              <span className={styles.exIdx}>{i + 1}</span>
              <div className={styles.exInfo}>
                <span className={styles.exName}>{ex.exercise_name || `Exercício #${ex.exercise_item_id}`}</span>
                <div className={styles.exBottom}>
                  {ex.muscle_group && (
                    <span className={styles.exMuscle}>{muscleGroupLabel(ex.muscle_group)}</span>
                  )}
                  <div className={styles.exMeta}>
                    {ex.sets_planned && (
                      <span className={styles.exStat}>
                        <span className={styles.exStatVal}>{ex.sets_planned}</span>
                        <span className={styles.exStatLabel}>séries</span>
                      </span>
                    )}
                    {ex.reps_planned && (
                      <span className={styles.exStat}>
                        <span className={styles.exStatVal}>{formatReps(ex.reps_planned)}</span>
                        <span className={styles.exStatLabel}>reps</span>
                      </span>
                    )}
                    {ex.rest_seconds && (
                      <span className={styles.exStat}>
                        <span className={styles.exStatVal}>{ex.rest_seconds.split(",")[0]}s</span>
                        <span className={styles.exStatLabel}>descanso</span>
                      </span>
                    )}
                    {ex.target_rir != null && (
                      <span className={styles.exStat} title="Repetições que você ainda conseguiria fazer ao terminar a série (intensidade do esforço).">
                        <span className={styles.exStatVal}>{ex.target_rir} reps</span>
                        <span className={styles.exStatLabel}>na reserva</span>
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
